import React, { useState } from "react";
import { Button, Box } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DownloadIcon from "@mui/icons-material/Download";
import Papa from "papaparse";
import { DataGrid } from "@mui/x-data-grid";
import axios from "axios";

export default function StudentStaffUpdatedCSV() {
  const [previewData, setPreviewData] = useState([]);
  const [csvFile, setCsvFile] = useState(null);

  const user = JSON.parse(sessionStorage.getItem("user"));
  const clientId = user?.client_id;
  const collegeId = user?.college_id;
  const user_email = user?.user_email;
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  // CSV headers - All updatable columns from core_mstr_student
  const csvHeaders = [
    "student_rollno",
    "admission_no",
    "admission_date",
    "aadhaar_no",
    "student_name",
    "father_name",
    "mother_name",
    "dob",
    "gender",
    "email",
    "mobile",
    "address",
    "admission_type",
    "previous_college",
    "transfer_date",
    "break_reason",
    "enroll_status",
    "student_status",
    "second_language",
    "seat_type",
  ];

  // Download CSV Template
  const handleDownloadTemplate = () => {
    const csvContent = csvHeaders.join(",") + "\n";
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "student_update_template.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // DataGrid columns
  const columns = [
    { field: "sno", headerName: "S.No", width: 70 },
    { field: "student_rollno", headerName: "Roll Number", width: 150 },
    { field: "admission_no", headerName: "Admission No", width: 150 },
    { field: "admission_date", headerName: "Admission Date", width: 130 },
    { field: "aadhaar_no", headerName: "Aadhaar No", width: 130 },
    { field: "student_name", headerName: "Student Name", width: 180 },
    { field: "father_name", headerName: "Father Name", width: 180 },
    { field: "mother_name", headerName: "Mother Name", width: 180 },
    { field: "dob", headerName: "Date of Birth", width: 120 },
    { field: "gender", headerName: "Gender", width: 100 },
    { field: "email", headerName: "Email", width: 200 },
    { field: "mobile", headerName: "Mobile", width: 130 },
    { field: "address", headerName: "Address", width: 200 },
    { field: "admission_type", headerName: "Admission Type", width: 150 },
    { field: "previous_college", headerName: "Previous College", width: 180 },
    { field: "transfer_date", headerName: "Transfer Date", width: 130 },
    { field: "break_reason", headerName: "Break Reason", width: 150 },
    { field: "enroll_status", headerName: "Enroll Status", width: 130 },
    { field: "student_status", headerName: "Student Status", width: 130 },
    { field: "second_language", headerName: "Second Language", width: 130 },
    { field: "seat_type", headerName: "Seat Type", width: 100 },
    {
      field: "errors",
      headerName: "Errors",
      width: 300,
      renderCell: (params) => (
        <span style={{ color: "red" }}>
          {params.value && params.value.length > 0 ? params.value.join(", ") : ""}
        </span>
      ),
    },
  ];

  // File selection
  const handleFileChange = (e) => {
    setCsvFile(e.target.files[0]);
  };

  // Parse + validate
  const handlePreview = () => {
    if (!csvFile) {
      alert("Please choose a CSV file first!");
      return;
    }

    Papa.parse(csvFile, {
      header: true,
      skipEmptyLines: true,
      complete: function (results) {
        const validatedData = results.data.map((row, index) => {
          let errors = [];

          // Mandatory field validation
          if (!row.student_rollno) {
            errors.push("Missing student_rollno");
          }

          // Email validation
          if (row.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row.email)) {
            errors.push("Invalid email format");
          }

          // Mobile validation (10 digits)
          if (row.mobile && !/^\d{10}$/.test(row.mobile)) {
            errors.push("Mobile must be 10 digits");
          }

          // Aadhaar validation (12 digits)
          if (row.aadhaar_no && !/^\d{12}$/.test(row.aadhaar_no)) {
            errors.push("Aadhaar must be 12 digits");
          }

          // Date format validation (YYYY-MM-DD)
          if (row.dob && !/^\d{4}-\d{2}-\d{2}$/.test(row.dob)) {
            errors.push("DOB must be YYYY-MM-DD");
          }

          if (row.admission_date && !/^\d{4}-\d{2}-\d{2}$/.test(row.admission_date)) {
            errors.push("Admission date must be YYYY-MM-DD");
          }

          if (row.transfer_date && !/^\d{4}-\d{2}-\d{2}$/.test(row.transfer_date)) {
            errors.push("Transfer date must be YYYY-MM-DD");
          }

          // Gender validation
          if (row.gender && !["M", "F", "O"].includes(row.gender)) {
            errors.push("Gender must be M, F, or O");
          }

          // Enum validations
          if (
            row.admission_type &&
            !["REGULAR", "LATERAL", "TRANSFER", "REJOIN"].includes(row.admission_type)
          ) {
            errors.push("Invalid admission_type");
          }

          if (
            row.enroll_status &&
            !["ADMITTED", "ENROLLED", "DROPPED"].includes(row.enroll_status)
          ) {
            errors.push("Invalid enroll_status");
          }

          if (
            row.student_status &&
            !["INACTIVE", "ACTIVE", "PASSED_OUT", "DETAINED", "TCOUT"].includes(row.student_status)
          ) {
            errors.push("Invalid student_status");
          }

          return {
            id: index + 1,
            sno: index + 1,
            ...row,
            errors,
          };
        });
        setPreviewData(validatedData);
      },
    });
  };

  // Confirm upload
  const handleConfirm = async () => {
    try {
      const validRows = previewData.filter((row) => !row.errors || row.errors.length === 0);
      if (validRows.length === 0) {
        alert("No valid rows to upload!");
        return;
      }

      const res = await axios.post(
        `${API_BASE_URL}/api/student-csv/${clientId}/${collegeId}/data`,
        { student_records: validRows }
      );

      alert(res.data.message || "Students updated successfully");
      setPreviewData([]);
      setCsvFile(null);
    } catch (err) {
      console.error(err);
      alert("Error uploading data: " + (err.response?.data?.message || err.message));
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Download Template Button */}
      <Button
        variant="contained"
        startIcon={<DownloadIcon />}
        onClick={handleDownloadTemplate}
        sx={{
          backgroundColor: "#388e3c",
          color: "#fff",
          "&:hover": { backgroundColor: "#2c6b2f" },
        }}
      >
        Download CSV
      </Button>

      {/* File upload */}
      <input
        type="file"
        accept=".csv"
        style={{ display: "none" }}
        id="upload-csv"
        onChange={handleFileChange}
      />
      <label htmlFor="upload-csv">
        <Button
          variant="contained"
          component="span"
          startIcon={<CloudUploadIcon />}
          sx={{
            ml: 2,
            backgroundColor: "#1976d2",
            color: "#fff",
            "&:hover": { backgroundColor: "#115293" },
          }}
        >
          Choose CSV
        </Button>
      </label>

      <Button
        sx={{
          ml: 2,
          backgroundColor: "#ff9800",
          color: "#fff",
          "&:hover": { backgroundColor: "#c77700" },
        }}
        variant="contained"
        onClick={handlePreview}
      >
        Preview
      </Button>

      {/* DataGrid preview */}
      {previewData.length > 0 && (
        <>
          <Box sx={{ mt: 3, height: 500 }}>
            <DataGrid
              rows={previewData}
              columns={columns}
              pageSize={10}
              rowsPerPageOptions={[5, 10, 25]}
              disableRowSelectionOnClick
            />
          </Box>
          <Button
            sx={{
              mt: 2,
              backgroundColor: "#4caf50",
              color: "#fff",
              "&:hover": { backgroundColor: "#357a38" },
            }}
            variant="contained"
            onClick={handleConfirm}
          >
            Confirm & Upload
          </Button>
        </>
      )}
    </Box>
  );
}
